import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useLogin } from "@/lib/auth/use-login";
import { translateApiError } from "@/lib/errors/error-map";
import { useAuthStore } from "@/lib/auth/store";
import { ChangePasswordDialog } from "@/features/auth/change-password-dialog";
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Token captured at login; used to authenticate the change-password call before
  // we clear the session. Kept local so re-renders don't lose it after store.clear().
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [flashMsg, setFlashMsg] = useState<string | null>(null);
  const clearSession = useAuthStore((s) => s.clear);
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const login = useLogin();
  const navigate = useNavigate();
  // from param for redirect-after-login
  const search = useSearch({ strict: false }) as { from?: string };

  // Cross-tab login: if another tab authenticates, the storage listener in
  // store.ts rehydrates this tab — redirect away from /login automatically.
  useEffect(() => {
    if (accessToken && user && !pendingToken) {
      const dest = search.from && search.from !== "/login" ? search.from : "/";
      void navigate({ to: dest });
    }
  }, [accessToken, user, pendingToken, navigate, search.from]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFlashMsg(null);
    login.mutate(
      { email, password },
      {
        onSuccess: (data) => {
          if (data.user.mustChangePassword) {
            // Hold the token locally; the dialog needs it because we'll clear the
            // session right after success so the user re-logs in with the new password.
            setPendingToken(data.accessToken);
            return;
          }
          const dest = search.from && search.from !== "/login" ? search.from : "/";
          void navigate({ to: dest });
        },
      },
    );
  }

  function handlePasswordChanged() {
    // Stale access token still carries must_change_password=true claim, so force
    // a fresh login rather than calling /auth/refresh — simpler and avoids edge cases.
    setPendingToken(null);
    clearSession();
    setPassword("");
    setFlashMsg("Đổi mật khẩu thành công. Vui lòng đăng nhập lại.");
  }

  const errorMsg = login.error ? translateApiError(login.error) : undefined;

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <h1 className="text-xl font-semibold">Đăng nhập</h1>
          <p className="text-sm text-muted-foreground">Nhật Minh Store — POS</p>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@nhatminh.vn"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">Mật khẩu</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>

            {flashMsg && (
              <p className="rounded-md bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400">
                {flashMsg}
              </p>
            )}

            {errorMsg && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {errorMsg}
              </p>
            )}
          </CardContent>

          <CardFooter>
            <Button type="submit" className="w-full" disabled={login.isPending}>
              {login.isPending ? "Đang đăng nhập…" : "Đăng nhập"}
            </Button>
          </CardFooter>
        </form>
      </Card>

      {pendingToken && (
        <ChangePasswordDialog
          accessToken={pendingToken}
          onSuccess={handlePasswordChanged}
          onClose={() => {
            setPendingToken(null);
            clearSession();
          }}
        />
      )}
    </div>
  );
}
